// /checkout/pay/:chargeId — exibe Pix QR / Boleto / link Cartão in-app.
// Polling de 5s no status do pagamento. Quando paga, redireciona pro `redirect`.
//
// VISUAL: kit do checkout público (`components/checkout-public/kit.tsx`) — o
// mesmo creme/preto do CheckoutFlow. Até 2026-08-24 esta tela usava os tokens
// escuros do painel ADMIN: o cliente pagava com Pix no checkout claro e caía
// numa tela preta na hora de escanear o QR. Há teste travando isso (pay.spec).

import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle2,
  CreditCard,
  Download,
  ExternalLink,
  Loader2,
  QrCode,
  Receipt,
} from 'lucide-react';
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  CheckoutErrorBox,
  CheckoutSection,
  CheckoutShell,
  CheckoutWaitingFooter,
  CopyCodeField,
} from '../../components/checkout-public/kit';
import { fetchPaymentDetails, fetchPaymentStatus } from '../../lib/public-checkout-api';

function formatBRL(amount: string): string {
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return amount;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function CheckoutPayPage(): JSX.Element {
  const { chargeId } = useParams<{ chargeId: string }>();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect') ?? '/checkout/success';

  const detailsQ = useQuery({
    queryKey: ['payment-details', chargeId],
    queryFn: () => fetchPaymentDetails(chargeId as string),
    enabled: Boolean(chargeId),
    retry: false,
  });

  // Polling de status — para automaticamente quando pago
  const statusQ = useQuery({
    queryKey: ['payment-status', chargeId],
    queryFn: () => fetchPaymentStatus(chargeId as string),
    enabled: Boolean(chargeId),
    refetchInterval: (q) => (q.state.data?.paid ? false : 5_000),
    retry: false,
  });

  // Quando pago → redireciona
  useEffect(() => {
    if (statusQ.data?.paid) {
      toast.success('Pagamento confirmado!');
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 1500);
    }
  }, [statusQ.data?.paid, redirectUrl]);

  if (detailsQ.isLoading) {
    return (
      <CheckoutShell title="PAGAMENTO">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-black/55" size={32} />
        </div>
      </CheckoutShell>
    );
  }

  if (detailsQ.isError || !detailsQ.data) {
    return (
      <CheckoutShell title="PAGAMENTO">
        <CheckoutErrorBox message="Não conseguimos carregar a cobrança. Verifique o link." />
      </CheckoutShell>
    );
  }

  const d = detailsQ.data;
  const paid = statusQ.data?.paid ?? false;

  return (
    <CheckoutShell title="PAGAMENTO">
      <div className="mx-auto w-full max-w-[560px]">
        {/* Total — tipografia do resumo do checkout */}
        <div className="mb-8 text-center">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/40">
            Total
          </div>
          <div className="tnum mt-1 text-[36px] font-black tracking-tight text-black">
            {formatBRL(d.amount)}
          </div>
        </div>

        {/* Banner de pago */}
        {paid && (
          <div className="mb-5 flex items-center gap-3 rounded-[12px] border border-teal-600/40 bg-teal-600/10 p-4">
            <CheckCircle2 size={20} strokeWidth={2} className="text-teal-600" />
            <div>
              <div className="text-[14px] font-semibold text-teal-700">Pagamento confirmado!</div>
              <div className="text-[12px] text-teal-700/80">Redirecionando…</div>
            </div>
          </div>
        )}

        {/* Conteúdo do método */}
        {!paid && d.payment_method === 'pix' && <PixSection details={d} />}
        {!paid && d.payment_method === 'boleto' && <BoletoSection details={d} />}
        {!paid && d.payment_method === 'credit_card' && <CardSection paymentUrl={d.payment_url} />}

        {!paid && <CheckoutWaitingFooter>Aguardando pagamento…</CheckoutWaitingFooter>}
      </div>
    </CheckoutShell>
  );
}

// =============================================================================
// Pix
// =============================================================================

function PixSection({
  details,
}: {
  details: {
    pix_qr_image: string | null;
    pix_payload: string | null;
    pix_expiration: string | null;
    payment_url: string | null;
  };
}): JSX.Element {
  if (!details.pix_qr_image || !details.pix_payload) {
    return (
      <CheckoutErrorBox
        message="Não conseguimos gerar o Pix agora. Use o link do Asaas como alternativa."
        fallbackUrl={details.payment_url}
      />
    );
  }

  return (
    <CheckoutSection title="Pague com Pix" icon={QrCode}>
      {/* QR Code */}
      <div className="flex justify-center">
        <div className="rounded-[12px] border border-black/10 bg-white p-3">
          <img
            src={`data:image/png;base64,${details.pix_qr_image}`}
            alt="QR Code Pix"
            className="h-[220px] w-[220px]"
          />
        </div>
      </div>

      <div className="mt-5">
        <CopyCodeField
          code={details.pix_payload}
          label="Ou copie o código"
          successMessage="Código Pix copiado!"
        />
      </div>

      <ol className="mt-5 space-y-2 text-[12px] text-black/55">
        <li>1. Abra o app do seu banco</li>
        <li>2. Escolha Pix → Pagar com QR Code ou Pix Copia e Cola</li>
        <li>3. Confirme o pagamento — confirmação chega em segundos</li>
      </ol>
    </CheckoutSection>
  );
}

// =============================================================================
// Boleto (só cobrança legada — assinatura nova nunca nasce em boleto)
// =============================================================================

function BoletoSection({
  details,
}: {
  details: {
    bank_slip_line: string | null;
    bank_slip_url: string | null;
    payment_url: string | null;
    due_date: string | null;
  };
}): JSX.Element {
  return (
    <CheckoutSection title="Pague com Boleto" icon={Receipt}>
      {details.due_date && (
        <div className="mb-3 text-[12px] text-black/55">
          Vencimento: <strong className="text-black">{details.due_date}</strong>
        </div>
      )}

      {details.bank_slip_line ? (
        <CopyCodeField
          code={details.bank_slip_line}
          label="Linha digitável"
          successMessage="Linha digitável copiada!"
        />
      ) : (
        <div className="text-[12px] text-black/55">Gerando boleto…</div>
      )}

      {details.bank_slip_url && (
        <div className="mt-4 flex flex-wrap gap-2">
          <a
            href={details.bank_slip_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-[10px] border border-black/10 bg-black/[0.035] px-3 py-2 text-[12px] font-medium text-black/70 hover:border-black/25"
          >
            <Download size={12} strokeWidth={2} />
            Baixar PDF
          </a>
        </div>
      )}

      <ol className="mt-5 space-y-2 text-[12px] text-black/55">
        <li>1. Copie a linha digitável ou baixe o PDF</li>
        <li>2. Pague no app do seu banco ou caixa eletrônico</li>
        <li>3. Compensação em até 2 dias úteis</li>
      </ol>
    </CheckoutSection>
  );
}

// =============================================================================
// Cartão (delega pro Asaas — PCI scope)
// =============================================================================

function CardSection({ paymentUrl }: { paymentUrl: string | null }): JSX.Element {
  // Em fluxo normal: cartão é cobrado IMEDIATAMENTE no submit do wizard.
  // Quando o cliente chega aqui, a cobrança já foi processada. Polling
  // detecta `paid: true` em poucos segundos e redireciona.
  //
  // Esse fallback aparece se Asaas demorar pra confirmar — mostra link
  // pro extrato (paymentUrl) e instrução amigável.
  return (
    <CheckoutSection title="Cartão processando…" icon={CreditCard}>
      <p className="text-[13px] text-black/55">
        Sua cobrança foi enviada pra autorização do banco emissor. Em geral é instantâneo — assim
        que confirmar, você é redirecionado.
      </p>

      {paymentUrl && (
        <a
          href={paymentUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-1 text-[12px] font-medium text-teal-700 hover:underline"
        >
          Ver detalhes da cobrança
          <ExternalLink size={11} strokeWidth={2} />
        </a>
      )}
    </CheckoutSection>
  );
}
